import { useState, useMemo } from 'react';
import styled from 'styled-components';
import { s } from '../components/PeriodStatsPage.styles';
import { downloadAsExcel } from '../../../utils/downloadAsExcel';

type PeriodAuctiontrendsProps = {
  periodSummary: string;
  region: string;
  usage: string;
  data: WeeklyTrendItem[];
  loading: boolean;
  error: string | null;
  initialVisibleCount?: number;
};

export type WeeklyTrendItem = {
  id: string;
  week: string;
  period: string;
  progress: number;
  wonCount: number;
  winRate: number; // 퍼센트(%)
  winPriceRate: number; // 퍼센트(%)
  avgBidders: number;
};

export default function PeriodAuctionTrend({
  periodSummary,
  region,
  usage,
  data = [],
  loading = false,
  error = null,
  initialVisibleCount = 4,
}: PeriodAuctiontrendsProps) {
  const [showAll, setShowAll] = useState(false);

  // 정렬 관련 로직 삭제: data 그대로 사용
  const rows = useMemo(() => [...data], [data]);

  const visible = useMemo(
    () => rows.slice(0, showAll ? rows.length : initialVisibleCount),
    [rows, showAll, initialVisibleCount],
  );

  const avg = useMemo(() => {
    const n = data.length || 1;
    if (data.length === 0) {
      return {
        progress: 0,
        wonCount: 0,
        winRate: 0,
        winPriceRate: 0,
        avgBidders: 0,
      };
    }
    const sum = data.reduce(
      (acc, cur) => ({
        progress: acc.progress + cur.progress,
        wonCount: acc.wonCount + cur.wonCount,
        winRate: acc.winRate + cur.winRate,
        winPriceRate: acc.winPriceRate + cur.winPriceRate,
        avgBidders: acc.avgBidders + cur.avgBidders,
      }),
      { progress: 0, wonCount: 0, winRate: 0, winPriceRate: 0, avgBidders: 0 },
    );
    return {
      progress: Math.round(sum.progress / n),
      wonCount: Math.round(sum.wonCount / n),
      winRate: +(sum.winRate / n).toFixed(1),
      winPriceRate: +(sum.winPriceRate / n).toFixed(1),
      avgBidders: +(sum.avgBidders / n).toFixed(1),
    };
  }, [data]);

  const buildRow = (r: WeeklyTrendItem) => ({
    주차: r.week,
    기간: r.period,
    진행건수: r.progress,
    낙찰건수: r.wonCount,
    '낙찰률(%)': Number(r.winRate.toFixed(1)),
    '낙찰가율(%)': Number(r.winPriceRate.toFixed(1)),
    평균응찰자수: Number(r.avgBidders.toFixed(1)),
  });

  const buildAvgRow = () => ({
    주차: '평균',
    기간: '-',
    진행건수: avg.progress,
    낙찰건수: avg.wonCount,
    '낙찰률(%)': Number(avg.winRate.toFixed(1)),
    '낙찰가율(%)': Number(avg.winPriceRate.toFixed(1)),
    평균응찰자수: Number(avg.avgBidders.toFixed(1)),
  });

  const handleDownloadExcel = () => {
    const rows = [...visible.map(buildRow), buildAvgRow()];
    downloadAsExcel(rows as any[], '주간_경매_동향');
  };

  return (
    <s.Wrap>
      <s.Header>
        <s.HeaderRow>
          <s.Img $bg="#E4EDFD">
            <img src="/assets/images/icon/blue_clock.svg" alt="" />
          </s.Img>
          <s.ExcelButton onClick={handleDownloadExcel} aria-label="엑셀 저장">
            <img src="/assets/images/icon/download-black.svg" alt="" />
          </s.ExcelButton>
        </s.HeaderRow>
        <s.TitleBox>
          <h3>주간 경매 동향</h3>
          <p>해당 기간 기준 4주~12주간 주요 경매 지표</p>
          <s.SelectedOptionBox>
            <s.SelectedOption>{periodSummary}</s.SelectedOption>
            <s.SelectedOption>{region} 지역</s.SelectedOption>
            <s.SelectedOption>{usage} 용도</s.SelectedOption>
          </s.SelectedOptionBox>
        </s.TitleBox>
      </s.Header>

      {loading ? (
        <LoaderWrap>
          <LoaderImg
            src="/assets/images/icon/loader.gif"
            alt="주간 경매 동향"
          />
        </LoaderWrap>
      ) : (
        <>
          <s.TableWrap>
            <s.Table>
              <thead>
                <tr>
                  <s.Th width="120px">
                    <SortHeader style={{ justifyContent: 'flex-start' }}>
                      기간
                    </SortHeader>
                  </s.Th>

                  <s.Th style={{ width: '20%' }} align="right">
                    <SortHeader>진행건수</SortHeader>
                  </s.Th>
                  <s.Th style={{ width: '20%' }} align="right">
                    <SortHeader>낙찰건수</SortHeader>
                  </s.Th>
                  <s.Th style={{ width: '20%' }} align="right">
                    <SortHeader>낙찰률</SortHeader>
                  </s.Th>
                  <s.Th style={{ width: '20%' }} align="right">
                    <SortHeader>낙찰가율</SortHeader>
                  </s.Th>
                  <s.Th style={{ width: '20%' }} align="right">
                    <SortHeader>평균 응찰자 수</SortHeader>
                  </s.Th>
                </tr>
              </thead>
              <tbody>
                {visible.map((row) => (
                  <tr key={row.id}>
                    <s.Td align="left">
                      <PeriodBox>
                        <Week>{row.week.split(' ')[1]}</Week>
                        {row.period}
                      </PeriodBox>
                    </s.Td>
                    <s.Td align="right">
                      {row.progress.toLocaleString('ko-KR')}
                    </s.Td>
                    <s.Td align="right">
                      {row.wonCount.toLocaleString('ko-KR')}
                    </s.Td>
                    <s.Td align="right">{row.winRate.toFixed(1)}%</s.Td>
                    <s.Td align="right">{row.winPriceRate.toFixed(1)}%</s.Td>
                    <s.Td align="right">{row.avgBidders.toFixed(1)}</s.Td>
                  </tr>
                ))}
                <s.AvgTr>
                  <s.TdAvg align="left" style={{ fontWeight: '500' }}>
                    평균
                  </s.TdAvg>
                  <s.TdAvg align="right">
                    {avg.progress.toLocaleString('ko-KR')}
                  </s.TdAvg>
                  <s.TdAvg align="right">
                    {avg.wonCount.toLocaleString('ko-KR')}
                  </s.TdAvg>
                  <s.TdAvg align="right">{avg.winRate.toFixed(1)}%</s.TdAvg>
                  <s.TdAvg align="right">
                    {avg.winPriceRate.toFixed(1)}%
                  </s.TdAvg>
                  <s.TdAvg align="right">{avg.avgBidders.toFixed(1)}</s.TdAvg>
                </s.AvgTr>
              </tbody>
            </s.Table>
          </s.TableWrap>

          {data.length > 4 && (
            <MoreButton onClick={() => setShowAll((v) => !v)}>
              <span>{showAll ? '접기' : '더보기'}</span>
              <img
                src="/assets/images/arrow/arrow-down-gray.svg"
                alt=""
                style={{
                  transform: showAll ? 'rotate(180deg)' : 'rotate(0deg)',
                }}
              />
            </MoreButton>
          )}
        </>
      )}
    </s.Wrap>
  );
}

const PeriodBox = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 4px;
`;

const Week = styled.span`
  color: #787d87;
`;

const MoreButton = styled.div`
  width: 100%;
  display: flex;
  gap: 6px;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  font-family: 'Spoqa Han Sans Neo';
  font-size: 14px;
  font-weight: 500;
  color: rgba(23, 30, 38, 0.76);
  border-radius: 6px;
  cursor: pointer;

  img {
    width: 16px;
    height: 16px;
    transition: transform 0.15s ease;
  }
`;

const LoaderWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 280px;
  width: 100%;
`;

const LoaderImg = styled.img`
  width: 24px;
  height: 24px;
`;

const SortHeader = styled.button`
  width: 100%;
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  gap: 4px;
  cursor: default; /* 클릭 불가처럼 보이게 */
  background: transparent;
  border: 0;
  padding: 0;
`;
